import { useEffect, useMemo, useRef, useState } from 'react';
import {
  resolveElement,
  groundStateFill,
  fillsToNotation,
  toSuperscript,
  AUFBAU_ORDER,
  FULL_BY_Z,
  type FullElement,
} from '../atomic/atomicData';
import {
  VizCard,
  NowYouTry,
  usePrefersReducedMotion,
  cueIsActive,
  cueHighlights,
  type VizCue,
} from '../vizShared';

// OrbitalDiagram — THE energy-level / sublevel diagram (the centerpiece). A
// vertical energy axis with every sublevel (1s, 2s, 2p, 3s, 3p, 4s, 3d, 4p, 5s,
// 4d, …) drawn at its real RELATIVE energy. Each sublevel is its set of orbital
// boxes (s=1, p=3, d=5, f=7) that fill with ↑/↓ electron arrows following the
// Madelung/aufbau diagonal rule + Hund's rule. Models the classic textbook
// "Orbital Energy Levels and Sublevels" chart.
//
//   • config.element ("Fe") or config.protons (26) picks the atom; defaults to
//     carbon. config.upTo caps which sublevels are drawn (e.g. "4p"); otherwise
//     the diagram spans from 1s up to one sublevel past the element's last.
//   • Self-demo on mount: walks the aufbau fill ONE sublevel per beat, then
//     settles full and offers "Now you try" (click any sublevel to spotlight it).
//   • Cue: cue.step walks the aufbau fill order (one sublevel per step);
//     cue.progress reveals electrons proportionally; cue.highlight spotlights a
//     named sublevel ("3d") or an element symbol; cue.play drives the walk.
//   • Shows the live spdf string (1s² 2s² 2p⁶ …).
// Resilient: clamps Z to 1–118, never throws on partial/empty config.

export interface OrbitalDiagramConfig {
  /** Element by symbol / name / Z, e.g. "Fe". */
  element?: string;
  /** Alternative to `element`: the number of protons (= electrons here). */
  protons?: number;
  /** Cap the diagram at this sublevel, e.g. "4p" (otherwise auto). */
  upTo?: string;
  title?: string;
}

interface Props {
  config?: OrbitalDiagramConfig;
  onComplete?: (result?: unknown) => void;
  cue?: VizCue;
}

const BOXES_PER_SUB: Record<string, number> = { s: 1, p: 3, d: 5, f: 7 };
const SUBSHELL_CAP: Record<string, number> = { s: 2, p: 6, d: 10, f: 14 };

// Per-sublevel-type accent (used for the box outline + label chip).
const SUB_COLOR: Record<string, string> = {
  s: '#5B8FF9',
  p: '#5AD8A6',
  d: '#F6BD16',
  f: '#E8684A',
};

function clamp(n: number, lo: number, hi: number) {
  return Math.max(lo, Math.min(hi, n));
}

// Relative energy of a sublevel. The aufbau (Madelung) sequence IS the order of
// increasing energy, so the index into AUFBAU_ORDER gives a faithful relative
// ranking — including the textbook overlaps (4s below 3d, etc.). We add a small
// per-n nudge so sublevels that the diagonal rule places close together still
// read as a tight cluster (matching the reference chart's spacing).
const AUFBAU_INDEX: Record<string, number> = Object.fromEntries(
  AUFBAU_ORDER.map((o, i) => [`${o.n}${o.sub}`, i]),
);

function energyOf(label: string): number {
  const idx = AUFBAU_INDEX[label];
  if (idx == null) return 999;
  // Compress slightly so higher levels bunch up like a real energy diagram.
  return idx;
}

// Distribute `count` electrons across a sublevel's boxes obeying Hund's rule:
// one spin-up in every box first, then pair with spin-down.
function distributeHund(count: number, boxes: number): Array<{ up: boolean; down: boolean }> {
  const slots = Array.from({ length: boxes }, () => ({ up: false, down: false }));
  let n = count;
  for (let i = 0; i < boxes && n > 0; i++) { slots[i].up = true; n--; }
  for (let i = 0; i < boxes && n > 0; i++) { slots[i].down = true; n--; }
  return slots;
}

interface Level {
  label: string;        // "3d"
  n: number;
  sub: 's' | 'p' | 'd' | 'f';
  orderIdx: number;     // index in the aufbau fill order
  energy: number;       // relative energy (for vertical placement)
  capacity: number;     // max electrons (2/6/10/14)
}

export default function OrbitalDiagram({ config = {}, onComplete, cue }: Props) {
  const reducedMotion = usePrefersReducedMotion();
  const hasCue = cueIsActive(cue);

  // Resolve the element (symbol/name/Z) or protons; default carbon.
  const element: FullElement =
    resolveElement(config.element ?? config.protons) ?? FULL_BY_Z[6];
  const z = clamp(element.z, 1, 118);

  // Which sublevels to draw. We span the aufbau order from 1s up to (and a touch
  // past) the element's last occupied sublevel, optionally capped by config.upTo.
  const levels = useMemo<Level[]>(() => {
    const ground = groundStateFill(z).fills;
    const lastFilledIdx = ground.reduce(
      (max, f) => Math.max(max, AUFBAU_INDEX[f.label] ?? 0),
      0,
    );
    // Show one extra empty sublevel above the highest occupied one for context.
    let cutoff = Math.min(AUFBAU_ORDER.length - 1, lastFilledIdx + 1);
    const cap = config.upTo ? AUFBAU_INDEX[config.upTo.trim().toLowerCase()] : undefined;
    if (cap != null) cutoff = cap;
    return AUFBAU_ORDER.slice(0, cutoff + 1).map((o, i) => ({
      label: `${o.n}${o.sub}`,
      n: o.n,
      sub: o.sub,
      orderIdx: i,
      energy: energyOf(`${o.n}${o.sub}`),
      capacity: SUBSHELL_CAP[o.sub],
    }));
  }, [z, config.upTo]);

  // Total number of aufbau steps available (for the stepper / cue.step).
  const totalSteps = levels.length;

  // Self-demo: walk the fill ONE sublevel per beat. `filledUpTo` is the count of
  // aufbau steps whose sublevels are fully revealed-as-filled (capped by z).
  const demoEnabled = !reducedMotion && !hasCue;
  const [step, setStep] = useState(() => (demoEnabled ? 0 : totalSteps));
  const [demoDone, setDemoDone] = useState(!demoEnabled);
  const [interacted, setInteracted] = useState(false);
  const [spotlight, setSpotlight] = useState<string | null>(null);

  // electrons placed so far, derived from step (each step fills one sublevel up
  // to its remaining-electron quota). Smooth proportional override via progress.
  const [electronsPlaced, setElectronsPlaced] = useState<number | null>(null);

  // ── Self-demo walk ──
  useEffect(() => {
    if (!demoEnabled) { setStep(totalSteps); setDemoDone(true); return; }
    setStep(0);
    setElectronsPlaced(null);
    const timers: number[] = [];
    for (let s = 1; s <= totalSteps; s++) {
      timers.push(window.setTimeout(() => setStep(s), 360 * s));
    }
    timers.push(window.setTimeout(() => setDemoDone(true), 360 * (totalSteps + 1)));
    return () => timers.forEach((t) => window.clearTimeout(t));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [z]);

  // ── Cue: highlight (sublevel label or element symbol), step, progress ──
  const cueTokens = useMemo(() => cueHighlights(cue), [cue]);
  useEffect(() => {
    const token = cueTokens[0];
    if (!token) { setSpotlight(null); return; }
    // A sublevel label like "3d" / "2p" spotlights that level.
    if (levels.some((l) => l.label === token)) {
      setSpotlight(token);
      setStep(totalSteps);
      setElectronsPlaced(null);
    } else {
      // Otherwise treat it as an element token (already resolved above for the
      // diagram); just clear any sublevel spotlight.
      setSpotlight(null);
    }
  }, [cueTokens, levels, totalSteps]);

  useEffect(() => {
    if (!cue) return;
    if (cue.step != null) {
      setDemoDone(true);
      setStep(clamp(Math.round(cue.step), 0, totalSteps));
      setElectronsPlaced(null);
    } else if (cue.play === true && cue.progress != null) {
      // animate the fill only while playing
      setDemoDone(true);
      setElectronsPlaced(clamp(Math.round(cue.progress * z), 0, z));
    } else {
      // paused / no progress → show the FULLY-filled diagram (null = full)
      setDemoDone(true);
      setElectronsPlaced(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cue?.step, cue?.progress, cue?.play, z, totalSteps]);

  // ── Compute per-level electron counts shown right now ──
  // If electronsPlaced is set (progress), distribute that many electrons over the
  // aufbau order. Otherwise fill the first `step` sublevels (each up to its
  // quota), capped by z — one sublevel per step.
  const shown = useMemo<Record<string, number>>(() => {
    const out: Record<string, number> = {};
    let remaining = electronsPlaced != null ? electronsPlaced : z;
    const stepCutoff = electronsPlaced != null ? totalSteps : step;
    for (let i = 0; i < levels.length; i++) {
      const l = levels[i];
      if (i >= stepCutoff || remaining <= 0) { out[l.label] = 0; continue; }
      const c = Math.min(l.capacity, remaining);
      out[l.label] = c;
      remaining -= c;
    }
    return out;
  }, [levels, step, electronsPlaced, z, totalSteps]);

  // Live spdf notation from what's currently shown.
  const notation = useMemo(() => {
    const fills = levels
      .filter((l) => (shown[l.label] ?? 0) > 0)
      .map((l) => ({ n: l.n, sub: l.sub, count: shown[l.label], label: l.label }));
    return fillsToNotation(fills);
  }, [levels, shown]);

  const totalShown = useMemo(
    () => levels.reduce((sum, l) => sum + (shown[l.label] ?? 0), 0),
    [levels, shown],
  );

  const markInteracted = (result?: unknown) => {
    if (!interacted) {
      setInteracted(true);
      onComplete?.(result ?? { type: 'orbital_diagram', element: element.symbol, z });
    }
  };

  // Clicking a sublevel spotlights it (and fully reveals the diagram).
  const onPickLevel = (label: string) => {
    setDemoDone(true);
    setStep(totalSteps);
    setElectronsPlaced(null);
    setSpotlight((s) => (s === label ? null : label));
    markInteracted({ type: 'orbital_diagram', element: element.symbol, z, sublevel: label });
  };

  // ── SVG layout ──
  // Vertical: higher energy = higher on screen (smaller y). We invert the energy
  // rank into a y coordinate. Width is responsive via viewBox.
  const PAD_T = 14;
  const PAD_B = 18;
  const ROW = 42;                       // vertical spacing per sublevel
  const AXIS_X = 46;                    // energy axis x
  const LABEL_X = AXIS_X + 14;          // sublevel label x
  const BOX_X0 = LABEL_X + 30;          // where the orbital boxes start
  const BOX_W = 20;
  const BOX_H = 22;
  const BOX_GAP = 4;
  const maxBoxes = Math.max(...levels.map((l) => BOXES_PER_SUB[l.sub]), 1);
  const stageW = BOX_X0 + maxBoxes * (BOX_W + BOX_GAP) + 10;
  const stageH = PAD_T + PAD_B + levels.length * ROW;

  // y for a level given its position in the (energy-sorted) display list.
  // Sort levels by energy descending so the LOWEST energy sits at the bottom.
  const byEnergyDesc = useMemo(
    () => [...levels].sort((a, b) => b.energy - a.energy),
    [levels],
  );
  const yFor = (label: string) => {
    const idx = byEnergyDesc.findIndex((l) => l.label === label);
    return PAD_T + idx * ROW + ROW / 2;
  };

  return (
    <VizCard
      eyebrow="Atomic structure · Sublevel energies"
      title={config.title ?? `${element.name} — orbital energy diagram`}
      subtitle="Each sublevel at its relative energy · electrons fill by the aufbau diagonal rule"
      reducedMotion={reducedMotion}
      hint="Higher = higher energy · ↑↓ obey Hund's rule · click a sublevel to spotlight it"
    >
      <div className="relative flex w-full flex-col gap-3">
        <NowYouTry show={demoDone && demoEnabled && !hasCue} />

        {/* Element + live notation */}
        <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-bold text-ws-ink">{element.symbol}</span>
            <span className="text-sm text-ws-ink-2">{element.name}</span>
            <span className="text-xs text-ws-ink-3">Z = {z}</span>
          </div>
          <div className="text-[11px] text-ws-ink-3">
            {totalShown} / {z} electrons
          </div>
        </div>

        <div className="rounded-md border border-ws-border bg-ws-surface-2 px-2.5 py-2">
          <span className="mr-2 text-[10px] uppercase tracking-wide text-ws-ink-3">spdf</span>
          <span className="font-mono text-sm text-ws-ink">{notation || '—'}</span>
        </div>

        {/* The energy-level diagram */}
        <div className="overflow-x-auto rounded-md border border-ws-border bg-ws-surface p-2">
          <svg
            viewBox={`0 0 ${stageW} ${stageH}`}
            width="100%"
            style={{ minWidth: stageW, maxWidth: stageW, display: 'block' }}
            role="img"
            aria-label={`Energy-level diagram for ${element.name}: ${notation}`}
          >
            {/* energy axis */}
            <line
              x1={AXIS_X} y1={PAD_T - 4} x2={AXIS_X} y2={stageH - PAD_B + 4}
              stroke="var(--ws-border, #cbd5e1)" strokeWidth={1.5}
            />
            {/* axis arrowhead */}
            <path
              d={`M ${AXIS_X} ${PAD_T - 8} l -4 8 l 8 0 z`}
              fill="var(--ws-ink-3, #64748b)"
            />
            <text
              x={AXIS_X - 8} y={PAD_T + 2}
              textAnchor="end"
              transform={`rotate(-90 ${AXIS_X - 8} ${stageH / 2})`}
              className="fill-ws-ink-3"
              style={{ fontSize: 9, letterSpacing: 0.5 }}
            >
              Energy →
            </text>

            {byEnergyDesc.map((l) => {
              const y = yFor(l.label);
              const color = SUB_COLOR[l.sub];
              const boxes = BOXES_PER_SUB[l.sub];
              const count = shown[l.label] ?? 0;
              const slots = distributeHund(count, boxes);
              const isSpot = spotlight === l.label;
              const dim = spotlight != null && !isSpot;
              return (
                <g
                  key={l.label}
                  style={{
                    opacity: dim ? 0.32 : 1,
                    cursor: 'pointer',
                    transition: reducedMotion ? undefined : 'opacity 220ms ease-out',
                  }}
                  onClick={() => onPickLevel(l.label)}
                >
                  {/* spotlight backing */}
                  {isSpot && (
                    <rect
                      x={AXIS_X + 2} y={y - ROW / 2 + 3}
                      width={stageW - AXIS_X - 6} height={ROW - 6}
                      rx={4}
                      fill="var(--ws-accent-quiet, rgba(99,102,241,0.12))"
                    />
                  )}
                  {/* tick from the axis to the level */}
                  <line
                    x1={AXIS_X} y1={y} x2={LABEL_X - 4} y2={y}
                    stroke="var(--ws-border, #cbd5e1)" strokeWidth={1}
                  />
                  {/* sublevel label */}
                  <text
                    x={LABEL_X} y={y}
                    dominantBaseline="middle"
                    className="fill-ws-ink-2"
                    style={{ fontSize: 12, fontWeight: 600 }}
                  >
                    {l.n}
                    <tspan style={{ fill: color }}>{l.sub}</tspan>
                  </text>

                  {/* orbital boxes */}
                  {slots.map((s, bi) => {
                    const bx = BOX_X0 + bi * (BOX_W + BOX_GAP);
                    const by = y - BOX_H / 2;
                    return (
                      <g key={bi}>
                        <rect
                          x={bx} y={by} width={BOX_W} height={BOX_H} rx={2.5}
                          fill="var(--ws-surface-2, rgba(0,0,0,0.02))"
                          stroke={color}
                          strokeWidth={1.4}
                        />
                        {/* up arrow */}
                        {s.up && (
                          <text
                            x={bx + BOX_W * 0.34} y={y + 1}
                            textAnchor="middle" dominantBaseline="middle"
                            style={{ fontSize: 14, fill: color, fontWeight: 700 }}
                          >
                            ↑
                          </text>
                        )}
                        {/* down arrow */}
                        {s.down && (
                          <text
                            x={bx + BOX_W * 0.66} y={y + 1}
                            textAnchor="middle" dominantBaseline="middle"
                            style={{ fontSize: 14, fill: color, fontWeight: 700 }}
                          >
                            ↓
                          </text>
                        )}
                      </g>
                    );
                  })}

                  {/* per-sublevel electron count (e.g. 2p⁶) once it has any */}
                  {count > 0 && (
                    <text
                      x={BOX_X0 + boxes * (BOX_W + BOX_GAP) + 2} y={y}
                      dominantBaseline="middle"
                      className="fill-ws-ink-3"
                      style={{ fontSize: 10, fontFamily: 'monospace' }}
                    >
                      {l.label}
                      {toSuperscript(count)}
                    </text>
                  )}
                </g>
              );
            })}
          </svg>
        </div>

        {/* Stepper through the aufbau order (mirrors cue.step) */}
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => { setDemoDone(true); setElectronsPlaced(null); setStep((s) => clamp(s - 1, 0, totalSteps)); markInteracted(); }}
            disabled={step <= 0}
            className="rounded-sm border border-ws-border bg-ws-tile px-2.5 py-1 text-xs font-medium text-ws-ink-2 transition-colors hover:bg-ws-surface-2 disabled:opacity-40"
          >
            ◀ Remove
          </button>
          <button
            type="button"
            onClick={() => { setDemoDone(true); setElectronsPlaced(null); setStep((s) => clamp(s + 1, 0, totalSteps)); markInteracted(); }}
            disabled={step >= totalSteps}
            className="rounded-sm border border-ws-accent bg-ws-accent px-2.5 py-1 text-xs font-semibold text-white transition-colors hover:bg-ws-accent-hover disabled:opacity-40"
          >
            Fill next ▶
          </button>
          <span className="text-[11px] text-ws-ink-3">
            sublevel {Math.min(step, totalSteps)} / {totalSteps}
            {step > 0 && step <= levels.length && (
              <span className="ml-1 font-mono text-ws-ink-2">({levels[step - 1].label})</span>
            )}
          </span>
        </div>
      </div>
    </VizCard>
  );
}
